import { UseDataQueryOptions } from '../../../hooks/useDataQuery/dataQuery.types';
import useDataQuery from '../../../hooks/useDataQuery/useDataQuery';
import useInfiniteDataQuery from '../../../hooks/useInfiniteDataQuery/useInfiniteDataQuery';
import { InfiniteDataQueryFnUtils } from '../../../hooks/useInfiniteDataQuery/utils/useInfiniteDataQueryFn.utils';
import { useApplicationContext } from '../../../modules/context/ApplicationContext/ApplicationContext';
import { queryClient } from '../../../modules/reactQuery/contexts/QueryClient/utils/queryClient';
import {
  getRachelChatFeedbackKey,
  getRachelChatKey,
  getRachelChatsListKey,
} from './rachelChats.keys';
import { findRachelChats, getRachelChat, getRachelChatFeedback } from './rachelChats.repository';
import { RachelChatOutput, RachelMessageFeedbackOutput } from './rachelChats.types';

const CHATS_PAGE_SIZE = 20;

interface UseRachelChatsListParams {
  search?: string;
  isEnabled?: boolean;
}

export const useRachelChatsList = ({ search, isEnabled = true }: UseRachelChatsListParams = {}) =>
  useInfiniteDataQuery<RachelChatOutput>({
    isEnabled,
    maxPages: null,
    limit: CHATS_PAGE_SIZE,
    queryKey: getRachelChatsListKey(search),
    defaultOrderBy: {},
    queryFn: InfiniteDataQueryFnUtils.fetcherWithCountAndParams(findRachelChats, { search }),
  });

export const usePrimeRachelChat = () => {
  const { team } = useApplicationContext();

  return (chat?: RachelChatOutput) => {
    if (!chat) {
      return;
    }
    queryClient.setQueryData([team?.id, ...getRachelChatKey(chat.id)], chat);
  };
};

export const useRachelChat = (chatId: string | null, options?: UseDataQueryOptions) =>
  useDataQuery<RachelChatOutput>(
    {
      queryKey: getRachelChatKey(chatId ?? ''),
      queryFn: async () => {
        const response = await getRachelChat(chatId ?? '');
        if (!response.ok || !response.data) {
          throw response.originalError;
        }
        return response.data;
      },
    },
    { enabled: !!chatId, ...options },
  );

export const useRachelChatFeedback = (chatId: string | null, options?: UseDataQueryOptions) =>
  useDataQuery<RachelMessageFeedbackOutput[]>(
    {
      queryKey: getRachelChatFeedbackKey(chatId ?? ''),
      queryFn: async () => {
        if (!chatId) {
          return [];
        }
        const response = await getRachelChatFeedback(chatId);
        if (!response.ok || !response.data) {
          throw response.originalError;
        }
        return response.data;
      },
    },
    { enabled: !!chatId, ...options },
  );

export const useRachelChatFeedbackMap = (chatId: string | null, options?: UseDataQueryOptions) => {
  const query = useRachelChatFeedback(chatId, options);

  const feedbackByMessageId: Record<string, RachelMessageFeedbackOutput> = {};
  query.data?.forEach((feedback) => {
    feedbackByMessageId[feedback.messageId] = feedback;
  });

  return { ...query, feedbackByMessageId };
};
